import React from "react";
import QuestionItemInfo from "./QuestionItemInfo";

import { FieldControl } from "react-reactive-form";
import { CFormInput } from "@coreui/react";
import useQuestions from "../hooks/useQuestions";
import NumericInput from "./NumericInput";
import MultiSelectDropdown from "./MultiSelectDropdown";
import PlainTableSingleSelectDropdown from "./PlainTableSingleSelectDropdown";
import PlainTableMultiSelectDropdown from "./PlainTableMultiSelectDropdown";
import QuillRichTextEditor from "./QuillRichTextEditor";

const QuestionItem = ({
  questionIndex = false,
  suffix = "",
  questionName,
  questionsName, //base question name for label lookup (useful when questionName has prefixes like "Comp")
  activator = false,
  questionInputType,
  questionInputChoice,
  globalId,
  disabled = false,
  required = false,
  otherOption = false,
  isMultiSelectDropdown = false,
  isPlainSingleSelectDropdown = false,
  isPlainMultiSelectDropdown = false,
  tables = true,
}) => {
  const { questionsData, wizardForm, studyId } = useQuestions();

  // Check if this question needs numeric validation
  const needsNumericValidation = [
    "qCutOffsForAdverseEventsTables",
    "qCutOffsForDemographicsTables",
    "qCutOffsForAbnormalLabTables",
  ].includes(questionName);
  // Use questionsName if provided, otherwise derive from questionName
  // Remove suffix and prefix (like "Comp") to get base question name
  const labelAttributeName =
    questionsName || (suffix ? questionName.split(suffix)[0] : questionName);
  // Remove any prefix like "Comp" from the label attribute name
  const baseQuestionName = labelAttributeName
    .replace(/^Comp/, "")
    .split("_")[0];

  return (
    <>
      {activator && (
        <div className="questions-content__container">
          {questionIndex ? (
            <div className="questions-content__questionIndexContainer">
              <span className="questions-content__label-index">
                {questionIndex}
              </span>
            </div>
          ) : (
            ""
          )}
          <div className={"questions-content__item"}>
            <div className={"questions-content__label"}>
              <span className={"questions-content__label-text"}>
                {questionsData.current.questions[baseQuestionName] ||
                  questionsData.current.questions[
                    labelAttributeName.split("_")[0]
                  ] ||
                  ""}
                {required && (
                  <span className="questions-content__required-asterisk">
                    {" "}
                    *
                  </span>
                )}
              </span>
              <QuestionItemInfo
                questionName={questionsName || baseQuestionName || questionName}
                notes={questionsData.current.questionsInfo}
              />
            </div>
            <div className={"questions-content__input"}>
              {isMultiSelectDropdown && (
                <MultiSelectDropdown
                  controlName={questionName}
                  formGroup={wizardForm}
                  disabled={disabled}
                  studyId={studyId}
                />
              )}
              {isPlainSingleSelectDropdown && (
                <PlainTableSingleSelectDropdown
                  controlName={questionName}
                  formGroup={wizardForm}
                  disabled={disabled}
                  studyId={studyId}
                  loadingMessage={
                    tables ? "Loading tables..." : "Loading figures..."
                  }
                  placeholder={
                    tables
                      ? "Type to search tables or select from the list of tables"
                      : "Type to search figures or select from the list of figures"
                  }
                />
              )}
              {isPlainMultiSelectDropdown && (
                <PlainTableMultiSelectDropdown
                  controlName={questionName}
                  formGroup={wizardForm}
                  disabled={disabled}
                  studyId={studyId}
                  tables={tables}
                  loadingMessage={
                    tables ? "Loading tables..." : "Loading figures..."
                  }
                  placeholder={
                    tables
                      ? "Type to search tables or select from the list of tables"
                      : "Type to search figures or select from the list of figures"
                  }
                />
              )}
              {questionInputType === "richtext" && (
                <QuillRichTextEditor controlName={questionName} />
              )}
              {questionInputType === "text" && needsNumericValidation && (
                <NumericInput
                  name={questionName}
                  disabled={disabled}
                  placeholder="Enter numeric value (e.g., 4)"
                />
              )}
              {questionInputType === "text" && !needsNumericValidation && (
                <FieldControl
                  name={questionName}
                  render={({ handler }) => (
                    <CFormInput
                      className={`input-text ${disabled ? "disabled" : ""}`}
                      type={"text"}
                      name={questionName}
                      readOnly={disabled}
                      {...handler()}
                    />
                  )}
                />
              )}
              {questionInputType === "number" && (
                <FieldControl
                  name={questionName}
                  render={({ handler }) => (
                    <CFormInput
                      className={`input-text ${disabled ? "disabled" : ""}`}
                      type={"number"}
                      name={questionName}
                      readOnly={disabled}
                      {...handler()}
                    />
                  )}
                />
              )}
              {questionInputType === "radio" &&
                Array.isArray(
                  questionsData.current?.choices?.[questionInputChoice]
                ) &&
                questionsData.current.choices[questionInputChoice].map(
                  (choice, i) => (
                    <FieldControl
                      key={`${questionName}-${i}_${globalId}`}
                      name={questionName}
                      render={({ handler, value }) => {
                        // Normalize both values to strings for consistent comparison
                        // This ensures boolean false/true values match string "false"/"true"
                        const choiceValueStr = String(choice.value);
                        const formValueStr =
                          value !== null && value !== undefined
                            ? String(value)
                            : "";

                        // Get handler props but override checked to ensure proper comparison
                        const handlerProps = handler("radio", choiceValueStr);

                        return (
                          <>
                            <input
                              className={`btn-check ${disabled ? "disabled" : ""}`}
                              id={`${questionName}-${i}_${globalId}`}
                              disabled={disabled}
                              {...handlerProps}
                              name={
                                globalId
                                  ? `${questionName}-${globalId}`
                                  : handlerProps.name
                              }
                              checked={formValueStr === choiceValueStr}
                            />
                            <label
                              className={`btn btn-light ${disabled ? "disabled" : ""}`}
                              htmlFor={`${questionName}-${i}_${globalId}`}>
                              {choice.title}
                            </label>
                          </>
                        );
                      }}
                    />
                  )
                )}
              {otherOption && (
                <FieldControl
                  name={questionName}
                  render={({ handler }) => (
                    <>
                      <input
                        className={`btn-check ${disabled ? "disabled" : ""}`}
                        id={`${questionName}radio-other${globalId}`}
                        disabled={disabled}
                        {...handler("radio", "Other")}
                      />
                      <label
                        className={`btn btn-light ${disabled ? "disabled" : ""}`}
                        htmlFor={`${questionName}radio-other${globalId}`}>
                        Other
                      </label>
                    </>
                  )}
                />
              )}
            </div>
            {otherOption &&
              wizardForm.controls[questionName] &&
              wizardForm.controls[questionName].value === "Other" && (
                <>
                  <span className={"empty-space-layout-helper"}></span>
                  <FieldControl
                    name={`${questionName}Other`}
                    render={({ handler }) => (
                      <CFormInput
                        className={`input-text ${disabled ? "disabled" : ""}`}
                        type={"text"}
                        name={`${questionName}Other`}
                        readOnly={true}
                        {...handler()}
                      />
                    )}
                  />
                </>
              )}
          </div>
        </div>
      )}
    </>
  );
};

export default QuestionItem;
